import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import FootnoteListNodeView from './FootnoteListNodeView.vue';
import FootnoteRefNodeView from './FootnoteRefNodeView.vue';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        footnote: {
            /** Insert a footnote reference at the current selection. */
            insertFootnoteRef: (options?: { content?: string }) => ReturnType;
            /** Insert (or ensure) the footnote list block at the end of the document. */
            insertFootnoteList: () => ReturnType;
        };
    }
}

// ── FootnoteRef — inline atom ─────────────────────────────────────────
// Each ref stores a stable footnoteId and the footnote text content.
// The display number is computed live by scanning all refs in document order.

export const FootnoteRefExtension = Node.create({
    name: 'footnoteRef',

    group: 'inline',

    inline: true,

    atom: true,

    addAttributes() {
        return {
            footnoteId: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-footnote-id'),
                renderHTML: (attrs) => ({
                    'data-footnote-id': attrs.footnoteId,
                }),
            },
            content: {
                default: '',
                parseHTML: (el) => el.getAttribute('data-content') ?? '',
                renderHTML: (attrs) => ({ 'data-content': attrs.content }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'sup[data-footnote-ref]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'sup',
            mergeAttributes(HTMLAttributes, { 'data-footnote-ref': '' }),
            0,
        ];
    },

    addNodeView() {
        return VueNodeViewRenderer(FootnoteRefNodeView);
    },

    addCommands() {
        return {
            insertFootnoteRef:
                ({ content = '' } = {}) =>
                ({ commands }) => {
                    const footnoteId =
                        typeof crypto !== 'undefined' && crypto.randomUUID
                            ? crypto.randomUUID()
                            : Math.random().toString(36).slice(2);

                    // Insert the ref, then ensure the list block exists
                    const inserted = commands.insertContent({
                        type: 'footnoteRef',
                        attrs: { footnoteId, content },
                    });

                    commands.insertFootnoteList();

                    return inserted;
                },
        };
    },
});

// ── FootnoteList — block ──────────────────────────────────────────────
// Singleton block that renders all footnotes in order.
// Only one instance should exist per document (enforced by insertFootnoteList).

export const FootnoteListExtension = Node.create({
    name: 'footnoteList',

    group: 'block',

    atom: true,

    draggable: false,

    addAttributes() {
        return {};
    },

    parseHTML() {
        return [{ tag: 'div[data-footnote-list]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, { 'data-footnote-list': '' }),
            0,
        ];
    },

    addNodeView() {
        return VueNodeViewRenderer(FootnoteListNodeView);
    },

    addCommands() {
        return {
            insertFootnoteList:
                () =>
                ({ state, commands }) => {
                    // Only insert if no footnoteList exists in the document already
                    let exists = false;
                    state.doc.descendants((node) => {
                        if (node.type.name === 'footnoteList') {
                            exists = true;

                            return false;
                        }

                        return true;
                    });

                    if (exists) {
                        return true;
                    }

                    return commands.insertContentAt(
                        {
                            from: state.doc.content.size - 1,
                            to: state.doc.content.size - 1,
                        },
                        { type: 'footnoteList' },
                    );
                },
        };
    },
});
